'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolsService } from '@/services/schools.service';
import { collegesService } from '@/services/colleges.service';
import type {
  SchoolsSearchParams,
  SchoolCreate,
  SchoolUpdate,
  SchoolResource,
} from '@/types/api';
import { toast } from 'sonner';

/**
 * Hook to search/list schools with pagination and filtering
 */
export function useSchools(params: SchoolsSearchParams = {}) {
  return useQuery({
    queryKey: ['schools', params],
    queryFn: () => schoolsService.searchSchools(params),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
  });
}

/**
 * Hook to get a single school by ID
 */
export function useSchool(schoolId: string | null, forDashboard = true) {
  return useQuery({
    queryKey: ['school', schoolId, { forDashboard }],
    queryFn: async () => {
      if (!schoolId) return null;
      
      // Fetch school data first
      const school = await schoolsService.getSchoolById(schoolId, forDashboard);
      
      // Skip schedule settings for colleges
      if (school.organizationType === 'college') {
        return {
          ...school,
          dayTypes: [],
          periodTypes: [],
          lunchSettings: { type: undefined },
        };
      }
      
      // Fetch schedule settings for non-college organizations
      const scheduleSettings = await schoolsService.getScheduleSettings(schoolId).catch(() => ({
        dayTypes: [],
        periodTypes: [],
        lunchType: undefined,
        lunchSlots: [],
      }));

      // Handle null scheduleSettings (defensive)
      if (!scheduleSettings) {
        return {
          ...school,
          dayTypes: [],
          periodTypes: [],
          lunchSettings: { type: undefined },
        };
      }

      // Parse lunch settings like old dashboard
      const lunchSettings: any =
        scheduleSettings.lunchType === 'waves'
          ? {
              type: scheduleSettings.lunchType,
              slots: scheduleSettings.lunchSlots?.map((slot) => ({
                id: slot.id || '',
                slot: slot.slot,
              })) || [],
              isAlphabetic: school.alphabeticLunchWaves || false,
            }
          : { type: scheduleSettings.lunchType };

      // Merge schedule settings into school data
      return {
        ...school,
        dayTypes: scheduleSettings.dayTypes?.map((dt) => ({
          id: dt.id || '',
          name: dt.name,
        })) || [],
        periodTypes: scheduleSettings.periodTypes?.map((pt) => ({
          id: pt.id || '',
          name: pt.name,
        })) || [],
        lunchSettings,
      };
    },
    enabled: !!schoolId,
    staleTime: 5 * 1000, // 5 seconds - keep data fresh
    refetchInterval: 10 * 1000, // Poll every 10 seconds (less frequent since it's heavier)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    refetchIntervalInBackground: false, // Only poll when tab is visible
  });
}

/**
 * Hook to get a specific resource for a school
 */
export function useSchoolResource<T = unknown>(
  schoolId: string | null,
  resource: SchoolResource,
  options: {
    dashboardMode?: boolean;
    includeUserGenerated?: boolean;
    enabled?: boolean;
  } = {}
) {
  const { dashboardMode = true, includeUserGenerated = false, enabled = true } = options;

  return useQuery<T>({
    queryKey: [
      'school',
      schoolId,
      'resource',
      resource,
      { dashboardMode, includeUserGenerated },
    ],
    queryFn: () =>
      schoolId
        ? schoolsService.getSchoolResource<T>(
            schoolId,
            resource,
            dashboardMode,
            includeUserGenerated
          )
        : Promise.reject(new Error('School ID is required')),
    enabled: !!schoolId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to create a new school
 */
export function useCreateSchool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      school,
      logo,
    }: {
      school: SchoolCreate;
      logo?: File;
    }) => {
      let logoId: string | undefined;

      // Upload logo first if provided
      if (logo) {
        const logoData = await schoolsService.uploadLogo(logo);
        logoId = logoData.id;
      }

      // Create school with logo ID
      return schoolsService.createSchool({
        ...school,
        logoId: logoId || school.logoId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast.success('School created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create school');
    },
  });
}

/**
 * Hook to update a school
 */
export function useUpdateSchool(schoolId: string, organizationType?: 'school' | 'college') {
  const queryClient = useQueryClient();
  const isCollege = organizationType === 'college';

  return useMutation({
    mutationFn: async ({
      school,
      logo,
    }: {
      school: SchoolUpdate;
      logo?: File;
    }) => {
      let logoId: string | undefined;

      // Upload logo first if provided
      if (logo) {
        const logoData = await schoolsService.uploadLogo(logo);
        logoId = logoData.id;
      }

      const updateData = {
        ...school,
        logoId: logoId || school.logoId,
      };

      try {
        if (isCollege) {
          return await collegesService.updateCollege(schoolId, updateData);
        }
        return await schoolsService.updateSchool(schoolId, updateData);
      } catch (error) {
        // If we successfully uploaded a logo, the update likely worked despite
        // CloudFront returning a cached error response. Return a synthetic success.
        if (logoId) {
          return { logoId } as any;
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools'] });
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('School updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update school');
    },
  });
}

/**
 * Hook to enable a feature for a school
 */
export function useEnableFeature(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (featureId: string) =>
      schoolsService.enableFeature(schoolId, featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Feature enabled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to enable feature');
    },
  });
}

/**
 * Hook to disable a feature for a school
 */
export function useDisableFeature(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (featureId: string) =>
      schoolsService.disableFeature(schoolId, featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Feature disabled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to disable feature');
    },
  });
}

/**
 * Convenience hooks for specific resources
 */
export function useSchoolCalendar(schoolId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['school', schoolId, 'calendar'],
    queryFn: () => (schoolId ? schoolsService.getCalendar(schoolId) : null),
    enabled: !!schoolId && enabled,
    staleTime: 5 * 1000, // 5 seconds - keep data fresh
    refetchInterval: 5 * 1000, // Poll every 5 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    refetchIntervalInBackground: false, // Only poll when tab is visible
  });
}

/**
 * Hook to update a single calendar day's schedule
 */
export function useUpdateDaySchedule(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      date,
      scheduleId,
      scheduleName,
    }: {
      date: string;
      scheduleId: string;
      scheduleName?: string;
    }) => schoolsService.updateDaySchedule(schoolId, date, scheduleId, scheduleName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'calendar'] });
      toast.success('Calendar day updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update calendar day');
    },
  });
}

/**
 * Hook to cancel a school day
 */
export function useCancelDay(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (date: string) => schoolsService.cancelDay(schoolId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'calendar'] });
      toast.success('Day canceled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to cancel day');
    },
  });
}

/**
 * Hook to bulk update calendar
 */
export function useBulkUpdateCalendar(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (calendarData: any[]) =>
      schoolsService.bulkUpdateCalendar(schoolId, calendarData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'calendar'] });
      toast.success('Calendar updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update calendar');
    },
  });
}

export function useSchoolSchedules(schoolId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['school', schoolId, 'schedules'],
    queryFn: () => (schoolId ? schoolsService.getSchedules(schoolId) : null),
    enabled: !!schoolId && enabled,
    staleTime: 5 * 1000, // 5 seconds - keep data fresh
    refetchInterval: 5 * 1000, // Poll every 5 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    refetchIntervalInBackground: false, // Only poll when tab is visible
  });
}

export function useSchoolYears(schoolId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['school', schoolId, 'school_years'],
    queryFn: () => (schoolId ? schoolsService.getSchoolYears(schoolId) : null),
    enabled: !!schoolId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to create a school year
 */
export function useCreateSchoolYear(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      startDate,
      endDate,
      name,
      emoji,
    }: {
      startDate: string;
      endDate: string;
      name?: string;
      emoji?: string;
    }) => {
      // Get school name from schoolId - need to fetch school first
      const school = await schoolsService.getSchoolById(schoolId, false);
      return schoolsService.createSchoolYear(school.schoolName, startDate, endDate, name, emoji);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'school_years'] });
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('School year created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create school year');
    },
  });
}

/**
 * Hook to update a school year
 */
export function useUpdateSchoolYear(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      schoolYearId,
      startDate,
      endDate,
      current,
    }: {
      schoolYearId: string;
      startDate: string;
      endDate: string;
      current?: boolean;
    }) => {
      // Get school name from schoolId - need to fetch school first
      const school = await schoolsService.getSchoolById(schoolId, false);
      return schoolsService.updateSchoolYear(
        schoolYearId,
        school.schoolName,
        startDate,
        endDate,
        current
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'school_years'] });
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('School year updated successfully. Note: The old school year entry may still exist in the system.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update school year');
    },
  });
}

/**
 * Hook to delete a school year
 * Note: The API doesn't support deleting school years, so this will show an error message
 */
export function useDeleteSchoolYear(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schoolYearId: string) => schoolsService.deleteSchoolYear(schoolYearId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'school_years'] });
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('School year deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete school year');
    },
  });
}

export function useSchoolCourses(
  schoolId: string | null,
  includeUserGenerated = true,
  enabled = true
) {
  return useQuery({
    queryKey: ['school', schoolId, 'courses', { includeUserGenerated }],
    queryFn: () =>
      schoolId
        ? schoolsService.getCourses(schoolId, includeUserGenerated)
        : null,
    enabled: !!schoolId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSchoolStaff(schoolId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['school', schoolId, 'staff'],
    queryFn: () => (schoolId ? schoolsService.getStaff(schoolId) : null),
    enabled: !!schoolId && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to update rotation pattern
 */
export function useUpdateRotationPattern(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rotationPattern: string[]) =>
      schoolsService.updateRotationPattern(schoolId, rotationPattern),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Rotation pattern updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update rotation pattern');
    },
  });
}

/**
 * Hook to get districts for dropdown selection
 */
export function useDistricts(search?: string) {
  return useQuery({
    queryKey: ['districts', search],
    queryFn: async () => {
      const { data } = await import('@/lib/api-client').then((mod) =>
        mod.apiClient.get<Array<{ districtCode: string; districtName: string }>>(
          '/v2/map/districts',
          {
            params: search ? { search } : undefined,
          }
        )
      );
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update schedule type
 */
export function useUpdateScheduleType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleType: string) =>
      schoolsService.updateScheduleType(schoolId, scheduleType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Schedule type updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update schedule type');
    },
  });
}

/**
 * Hook to update lunch slots (number of lunch waves)
 */
export function useUpdateLunchSlots(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (numSlots: number) =>
      schoolsService.updateLunchSlots(schoolId, numSlots),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Lunch waves updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update lunch waves');
    },
  });
}

/**
 * Hook to create a lunch class
 */
export function useCreateLunchClass(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => schoolsService.createLunchClass(schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Lunch class created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create lunch class');
    },
  });
}

/**
 * Hook to delete a lunch class
 */
export function useDeleteLunchClass(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      schoolsService.deleteLunchClass(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Lunch class deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete lunch class');
    },
  });
}

/**
 * Hook to create a day type
 */
export function useCreateDayType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      schoolsService.createDayType(schoolId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Day type created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create day type');
    },
  });
}

/**
 * Hook to update a day type
 */
export function useUpdateDayType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      schoolsService.updateDayType(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Day type updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update day type');
    },
  });
}

/**
 * Hook to delete a day type
 */
export function useDeleteDayType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dayTypeId: string) =>
      schoolsService.deleteDayType(dayTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Day type deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete day type');
    },
  });
}

/**
 * Hook to create a period type
 */
export function useCreatePeriodType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      schoolsService.createPeriodType(schoolId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Period type created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create period type');
    },
  });
}

/**
 * Hook to update a period type
 */
export function useUpdatePeriodType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      schoolsService.updatePeriodType(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Period type updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update period type');
    },
  });
}

/**
 * Hook to delete a period type
 */
export function useDeletePeriodType(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (periodTypeId: string) =>
      schoolsService.deletePeriodType(periodTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
      toast.success('Period type deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete period type');
    },
  });
}

/**
 * Hook to get schools map data with GeoJSON format
 * Cached for 24 hours since map data doesn't change frequently
 */
export function useSchoolsMap(filters?: string) {
  return useQuery({
    queryKey: ['schools', 'map', filters],
    queryFn: () => schoolsService.getSchoolsGeoJSON(filters),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - map data doesn't change often
    gcTime: 48 * 60 * 60 * 1000, // Keep in cache for 48 hours
  });
}

